import { useQuery } from '@tanstack/react-query'
import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  Timestamp,
  where,
  type OrderByDirection,
  type QueryConstraint,
} from 'firebase/firestore'
import { db, hasValidFirebaseEnv } from '@/lib/firebase'

interface CollectionOptions {
  orderByField?: string
  direction?: OrderByDirection
  limitCount?: number
  equals?: Array<[string, string | number | boolean]>
  /** Filter `>=`, mis. untuk menghitung user yang aktif sejak tanggal tertentu. */
  greaterThanOrEqual?: Array<[string, Date | number]>
  enabled?: boolean
}

function buildQuery(path: string[], options?: CollectionOptions) {
  const constraints: QueryConstraint[] = []

  for (const [field, value] of options?.equals ?? []) {
    constraints.push(where(field, '==', value))
  }

  for (const [field, value] of options?.greaterThanOrEqual ?? []) {
    constraints.push(
      where(field, '>=', value instanceof Date ? Timestamp.fromDate(value) : value),
    )
  }

  if (options?.orderByField) {
    constraints.push(orderBy(options.orderByField, options.direction ?? 'desc'))
  }

  if (options?.limitCount) {
    constraints.push(limit(options.limitCount))
  }

  return query(collection(db, path.join('/')), ...constraints)
}

export function useCollectionCount(path: string[], options?: CollectionOptions) {
  return useQuery({
    queryKey: ['collection-count', path.join('/'), options],
    queryFn: async () => {
      if (!hasValidFirebaseEnv) return 0
      const snapshot = await getCountFromServer(buildQuery(path, options))
      return snapshot.data().count
    },
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  })
}

export function useCollectionRecords<T>(
  path: string[],
  options?: CollectionOptions,
) {
  return useQuery({
    queryKey: ['collection-records', path.join('/'), options],
    queryFn: async () => {
      if (!hasValidFirebaseEnv) return [] as unknown as T[]
      const snapshot = await getDocs(buildQuery(path, options))
      return snapshot.docs.map((item) => ({ id: item.id, ...item.data() })) as unknown as T[]
    },
    staleTime: 2 * 60 * 1000,
    enabled: options?.enabled ?? true,
  })
}

export function useDocumentRecord<T>(
  path: string[],
  enabled = true,
) {
  return useQuery({
    queryKey: ['document-record', path.join('/')],
    queryFn: async () => {
      if (!hasValidFirebaseEnv) return null
      const snapshot = await getDoc(doc(db, path.join('/')))
      if (!snapshot.exists()) return null
      return { id: snapshot.id, ...snapshot.data() } as unknown as T
    },
    staleTime: 5 * 60 * 1000,
    enabled,
  })
}
